const INSTALLMENTS_PER_MONTH = {
    daily: 30,
    weekly: 4,
    monthly: 1,
};
/**
 * Resolve the financed total (full installment total) for a tenor (ADR-0014):
 *  - `fixed`      → the flat total.
 *  - `per_period` → `cost + perMonthMargin × tenor`; margin is an absolute
 *                   amount/month, or a % (bps) of cost/month. Cost is the base,
 *                   so a short tenor never prices below cost.
 *  - `per_tenor`  → the explicit total for that tenor (throws if none).
 */
export function financedLendingPrice(lendingPrice, tenorMonths, costMinor) {
    switch (lendingPrice.mode) {
        case "fixed":
            return lendingPrice.totalMinor;
        case "per_period": {
            const perMonthMargin = lendingPrice.perPeriod.basis === "amount"
                ? lendingPrice.perPeriod.perPeriodMinor
                : Math.round((costMinor * lendingPrice.perPeriod.rateBps) / 10_000);
            return costMinor + perMonthMargin * tenorMonths;
        }
        case "per_tenor": {
            const entry = lendingPrice.perTenor.find((e) => e.tenorMonths === tenorMonths);
            if (entry === undefined) {
                throw new RangeError(`per_tenor: no financed price configured for ${tenorMonths} months`);
            }
            return entry.totalMinor;
        }
    }
}
/** Split `total` into `count` integer parts that sum exactly to `total`. */
function splitEvenly(total, count) {
    const parts = [];
    let prev = 0;
    for (let i = 1; i <= count; i++) {
        const cumulative = Math.round((total * i) / count);
        parts.push(cumulative - prev);
        prev = cumulative;
    }
    return parts;
}
function addDays(date, days) {
    return new Date(date.getTime() + days * 86_400_000);
}
/** Add months keeping the day-of-month, clamped to the target month's last day. */
function addMonths(date, months) {
    const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(date.getUTCDate(), lastDay));
    target.setUTCHours(date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
    return target;
}
function dueDate(start, frequency, n) {
    switch (frequency) {
        case "daily":
            return addDays(start, n);
        case "weekly":
            return addDays(start, n * 7);
        case "monthly":
            return addMonths(start, n);
    }
}
/**
 * Build a repayment schedule from device pricing + loan terms.
 *
 * @throws RangeError when tenor < 1, amounts are negative/non-integer, the
 *   down payment exceeds the device cost, or the lending price is below cost
 *   (which would make interest negative).
 */
export function generateSchedule(input) {
    const { costMinor, downPaymentMinor, frequency, tenorMonths, startAt } = input;
    if (!Number.isInteger(tenorMonths) || tenorMonths < 1) {
        throw new RangeError("tenorMonths must be a positive integer");
    }
    if (!Number.isInteger(costMinor) || costMinor < 0) {
        throw new RangeError("costMinor must be a non-negative integer");
    }
    if (!Number.isInteger(downPaymentMinor) || downPaymentMinor < 0) {
        throw new RangeError("downPaymentMinor must be a non-negative integer");
    }
    if (downPaymentMinor > costMinor) {
        throw new RangeError("down payment must be <= device cost");
    }
    const lendingPriceMinor = financedLendingPrice(input.lendingPrice, tenorMonths, costMinor);
    // Deposit offsets cost (ADR-0014): the financed total is the FULL installment
    // total; principal recovers the remaining cost after the deposit, interest is
    // everything else. The floor keeps interest non-negative.
    const principalTotalMinor = costMinor - downPaymentMinor;
    if (lendingPriceMinor < principalTotalMinor) {
        throw new RangeError("financed total must be >= cost - downPayment (non-negative interest)");
    }
    const interestTotalMinor = lendingPriceMinor - principalTotalMinor;
    const count = INSTALLMENTS_PER_MONTH[frequency] * tenorMonths;
    const principals = splitEvenly(principalTotalMinor, count);
    const interests = splitEvenly(interestTotalMinor, count);
    const installments = [];
    for (let i = 0; i < count; i++) {
        installments.push({
            installmentNumber: i + 1,
            dueAt: dueDate(startAt, frequency, i + 1),
            principalMinor: principals[i] ?? 0,
            interestMinor: interests[i] ?? 0,
            feesMinor: 0,
        });
    }
    return {
        lendingPriceMinor,
        downPaymentMinor,
        principalTotalMinor,
        interestTotalMinor,
        installments,
    };
}
//# sourceMappingURL=schedule.js.map